/**
 * Purchase-order composer form schema (string-typed, operator-facing).
 *
 * Mirrors the bill composer's `lib/form-schema.ts` shape but simpler — a
 * PO has no AI extraction, no attachments, no approval workflow. Line
 * inputs stay string-typed exactly as the `purchaseOrders` wire contract
 * expects (`poLineInput`): qty/rate/discount/tax are operator strings the
 * domain re-computes server-side. Paise totals are computed client-side
 * for preview and sent as STRINGIFIED integer paise.
 *
 * @see packages/api-contract/src/procedures/purchase-orders.ts
 */
import { z } from "zod";

/** One editable line in the PO composer (all string fields). */
export interface PoLineItem {
  id: string;
  description: string;
  hsn: string;
  qty: string;
  unit: string;
  rate: string;
  discountRate: string;
  taxRate: string;
}

const poLineFormSchema = z.object({
  id: z.string(),
  description: z.string(),
  hsn: z.string(),
  qty: z.string(),
  unit: z.string(),
  rate: z.string(),
  discountRate: z.string(),
  taxRate: z.string(),
});

export const poFormSchema = z.object({
  vendorId: z.string().min(1, "Pick a vendor"),
  orderDate: z.string().min(1, "Order date is required"),
  expectedDate: z.string(),
  vendorNote: z.string(),
  internalNote: z.string(),
  lineItems: z.array(poLineFormSchema).min(1, "Add at least one line item"),
});

export type PoFormValues = z.infer<typeof poFormSchema>;

export function emptyPoLine(index: number): PoLineItem {
  return {
    id: `line-${index}`,
    description: "",
    hsn: "",
    qty: "1",
    unit: "pcs",
    rate: "",
    discountRate: "0",
    taxRate: "18",
  };
}

export const EMPTY_PO_FORM: PoFormValues = {
  vendorId: "",
  orderDate: todayIso(),
  expectedDate: "",
  vendorNote: "",
  internalNote: "",
  lineItems: [emptyPoLine(1)],
};

/**
 * Wall-clock default for the order-date field. Acceptable in app code —
 * AGENTS.md §7 rule 12 (`no new Date()`) applies strictly to domain code;
 * UI date defaults are not part of the financial-truth surface.
 */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
